// userStorage.ts — shared Supabase Storage helpers for user-uploaded documents.
// Covers the two user-facing buckets: profile-documents (verification, tax,
// payslips, structure deeds) and property-documents (property attachments).
// Lifted from ProfilePage.tsx (J11 / Day 47) so StructureModal and
// PropertyDetailPage can reuse the same canonical pattern. See LESSON L21.
//
// Returns Result types per LESSON L1 — async writes never throw, never
// silently swallow errors. Callers display the error string.

import { supabase, isSupabaseEnabled } from "./supabase";

export type UserStorageBucket = "profile-documents" | "property-documents";

export interface UploadResult {
  filePath: string;
  fileSize: number;
}

export type UploadOutcome =
  | { ok: true; result: UploadResult }
  | { ok: false; error: string };

/** Make a filename safe for use as a Storage object key, with a timestamp
 *  suffix to avoid collisions. Identical logic to the J11 fix. */
export function sanitiseFilename(name: string): string {
  const ext = name.split(".").pop() ?? "bin";
  const base = name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_\-]/g, "_").slice(0, 60);
  return `${base}_${Date.now()}.${ext}`;
}

/** Upload a file to the given bucket. pathPrefix is everything under the
 *  bucket root EXCEPT the filename — the function appends
 *  `/${sanitiseFilename(file.name)}`. The first segment of pathPrefix MUST
 *  be the authenticated user's id; defensive check rejects mismatches. */
export async function uploadToStorage(
  bucket: UserStorageBucket,
  pathPrefix: string,
  file: File,
): Promise<UploadOutcome> {
  if (!isSupabaseEnabled() || !supabase) {
    return { ok: false, error: "Storage unavailable" };
  }
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { ok: false, error: "Not signed in" };
  const firstSegment = pathPrefix.split("/")[0];
  if (firstSegment !== user.id) {
    return { ok: false, error: "Path prefix does not match authenticated user" };
  }
  const filePath = `${pathPrefix}/${sanitiseFilename(file.name)}`;
  const { error } = await supabase.storage.from(bucket).upload(filePath, file, { upsert: false });
  if (error) {
    return { ok: false, error: `Upload failed: ${error.message}` };
  }
  return { ok: true, result: { filePath, fileSize: file.size } };
}

/** Open a signed URL (1hr TTL) in a new tab. Best-effort — silent on
 *  failure since download is a user-initiated action they can retry. */
export async function downloadFromStorage(
  bucket: UserStorageBucket,
  filePath: string,
): Promise<void> {
  if (!isSupabaseEnabled() || !supabase) return;
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(filePath, 3600);
  if (error || !data?.signedUrl) return;
  window.open(data.signedUrl, "_blank");
}

/** Delete a file from storage. Best-effort — silent on failure since
 *  storage cleanup is a hygiene operation, not a correctness gate. */
export async function deleteFromStorage(
  bucket: UserStorageBucket,
  filePath: string,
): Promise<void> {
  if (!isSupabaseEnabled() || !supabase) return;
  await supabase.storage.from(bucket).remove([filePath]);
}
